import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      const paymentParam = searchParams.get('payment');

      if (paymentParam === 'cancelled') {
        setPaymentStatus('cancelled');
        setVerifying(false);
        return;
      }

      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/payment/verify/${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Payment verification failed');
        }

        const data = await response.json();
        
        if (data.success) {
          setPaymentStatus('success');
          setUserData({
            name: data.user_name,
            email: data.user_email
          });
        } else {
          setPaymentStatus('failed');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setPaymentStatus('error');
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  const handleContinue = () => {
    // Navigate to questionnaire with user data
    navigate('/', { 
      state: { 
        startQuestionnaire: true,
        userData: userData 
      } 
    });
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-purple-500 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Verifying Payment...
          </h2>
          <p className="text-gray-600">Please wait a moment</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full glass-effect rounded-3xl p-8 text-center shadow-2xl">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-800 mb-3">
            Payment Successful!
          </h1>
          
          <p className="text-gray-600 mb-8 text-lg leading-relaxed">
            Thank you for your purchase! Your payment of <strong>£25</strong> has been confirmed. 
            Let's start creating your autobiography.
          </p>

          <Button
            onClick={handleContinue}
            className="btn-primary w-full text-white py-6 text-lg font-semibold rounded-full border-0"
          >
            Start My Story
          </Button>

          <p className="text-sm text-gray-500 mt-4">
            A receipt has been sent to {userData?.email}
          </p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full glass-effect rounded-3xl p-8 text-center shadow-2xl">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full mb-6">
            <XCircle className="w-12 h-12 text-white" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-800 mb-3">
            Payment Cancelled
          </h1>
          
          <p className="text-gray-600 mb-8">
            Your payment was cancelled. No charges have been made to your account.
          </p>

          <Button
            onClick={() => navigate('/')}
            className="btn-primary w-full text-white py-6 text-lg font-semibold rounded-full border-0"
          >
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  // Error or failed state
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full glass-effect rounded-3xl p-8 text-center shadow-2xl">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-400 to-red-500 rounded-full mb-6">
          <XCircle className="w-12 h-12 text-white" />
        </div>
        
        <h1 className="text-3xl font-bold text-gray-800 mb-3">
          Payment Failed
        </h1>
        
        <p className="text-gray-600 mb-8">
          We couldn't process your payment. Please try again or contact support if the issue persists.
        </p>

        <div className="space-y-3">
          <Button
            onClick={() => navigate('/')}
            className="btn-primary w-full text-white py-6 text-lg font-semibold rounded-full border-0"
          >
            Try Again
          </Button>
          
          <Button
            variant="outline"
            onClick={() => window.location.href = 'mailto:hello@lifeecho.com'}
            className="w-full rounded-full"
          >
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
